import React, { memo } from 'react';

import classNames from 'classnames';
import { motion } from 'framer-motion';
import { ResponsivePropsType } from 'styles/theme/theme.type';

import styles from './mText.module.css';

type ResponsiveSizeType = ResponsivePropsType<MtextSizeType>;
type ResponsiveWeightType = ResponsivePropsType<WeightType>;

type WeightType = 'regular' | 'bold' | 'semibold' | 'medium';

export type MtextSizeType =
  | 'extraLargeBody'
  | 'largeBody'
  | 'body'
  | 'smallBody'
  | 'tinyBody'
  | 'headerSize'
  | 'subHeader';

export type MTextAnimatedPropsType = React.ComponentPropsWithoutRef<typeof motion.span>;

type DefaultPropsType = {
  children: React.ReactNode;
  size?: ResponsiveSizeType | MtextSizeType;
  weight?: ResponsiveWeightType | WeightType;
  className?: string;
};

type PropsType = DefaultPropsType &
  (
    | {
        animatedProps: MTextAnimatedPropsType;
      }
    | { animatedProps?: never }
  );

export const MText: React.FC<PropsType> = memo(
  ({ children, size = 'body', weight = 'regular', className, animatedProps }) => {
    const isResponsiveSize = typeof size === 'object';
    const isResponsiveWeight = typeof weight === 'object';

    const getSizeClassName = () => {
      if (!isResponsiveSize) return styles[size];

      return classNames(
        Object.entries(size).reduce<string[]>((acc, [breakpoint, size]) => {
          if (size) {
            acc.push(styles[`${breakpoint}_${size}`]);
          }

          return acc;
        }, [])
      );
    };

    const getWeightClassName = () => {
      if (!isResponsiveWeight) return styles[`${weight}Weight`];

      return classNames(
        Object.entries(weight).reduce<string[]>((acc, [breakpoint, weight]) => {
          if (weight) {
            acc.push(styles[`${breakpoint}_${weight}`]);
          }

          return acc;
        }, [])
      );
    };

    const defaultProps = {
      className: classNames(getSizeClassName(), getWeightClassName(), className),
    };

    if (animatedProps) {
      return (
        <motion.span {...animatedProps} {...defaultProps}>
          {children}
        </motion.span>
      );
    }

    return <span {...defaultProps}>{children}</span>;
  }
);
